import { Dialog, DialogId, DialogType } from '../../../../src/feature/narrator/narrator.model';
import { hasItem } from '../../inventory/helpers';
import { weightedPick } from '../../random';

// Fishing is a *session*, not a cast. Starting one needs a handful of worms,
// spent up front as the bait for the whole outing. You play through a single
// bite with one choice, then the rest of the day is narrated and you take home a
// haul that depends on the choice you made.

// Pull it in now = the safe play: a modest, sure haul (small chance of a joke).
function resolvePullNow(): DialogId {
  return weightedPick([
    ['haul_safe_a', 45],
    ['haul_safe_b', 45],
    ['bust_boot', 10],
  ] as const);
}

// Let it run = the gamble: a bigger haul, the one that got away, or a joke.
function resolveLetRun(): DialogId {
  return weightedPick([
    ['haul_big_pike', 30],
    ['haul_big_eel', 30],
    ['got_away', 25],
    ['bust_boot', 8],
    ['bust_eeltrap', 7],
  ] as const);
}

const AWAY: { text: string; nextDialogId: DialogId } = { text: 'Step back from the water.', nextDialogId: 'start' };

export const dialog: Dialog[] = [
  {
    id: 'start',
    type: DialogType.description,
    text: 'The fisherman stands in the shallow water of the Alzette. He watches his line. He turns when he hears you and gives a small nod.',
    options: [
      { text: 'Ask what fish he catches here.', nextDialogId: 'fish_list' },
      { text: 'Stand and watch for a moment.', nextDialogId: 'watching', condition: () => !hasItem('rod') },
      { text: 'Where can I find worms?', nextDialogId: 'worms', condition: () => hasItem('rod') && !hasItem('worm') },
      { text: 'Fish here.', nextDialogId: 'session_bite', condition: () => hasItem('rod') && hasItem('worm') },
    ],
  },
  {
    id: 'fish_list',
    type: DialogType.dialog,
    text: 'Trout, where the water runs fast. Chub, where it is slow. A pike if you are lucky. Eels toward evening. The rest is between you and the river, as the Alzette runs.',
    options: [
      { text: 'Could I try fishing too?', nextDialogId: 'offer', condition: () => !hasItem('rod') },
      { text: 'Goodbye and good fishing.', nextDialogId: 'start' },
    ],
  },
  {
    id: 'watching',
    type: DialogType.description,
    text: 'He lets you stand a while. The line moves slowly in the water. After a long pause he looks up, then waves you over with his hand.',
    options: [
      { text: '(Step closer.)', nextDialogId: 'offer', condition: () => !hasItem('rod') },
      { text: '(Walk away.)', nextDialogId: 'start' },
    ],
  },
  {
    id: 'offer',
    type: DialogType.dialog,
    text: 'You have the look of a man with nothing to do. I have a spare rod here. It is old, but it still works. Take it. You will need worms first, though. The fish do not catch themselves.',
    options: [
      { text: 'Take the rod and thank him.', nextDialogId: 'accept' },
      { text: 'Where should I dig for worms?', nextDialogId: 'worms' },
    ],
  },
  {
    id: 'worms',
    type: DialogType.dialog,
    text: 'Go east from here. Past the crossroad you will find a big **oak tree**. The earth under its roots is dark and wet, full of fat worms. A handful of them is better than any other bait.',
    options: [
      { text: "I'll go and dig.", nextDialogId: 'accept', condition: () => !hasItem('rod') },
      { text: 'Thank you.', nextDialogId: 'start', condition: () => hasItem('rod') },
    ],
  },
  {
    id: 'accept',
    type: DialogType.dialog,
    text: 'Here. Hold it carefully. It has been with me all winter. Good fishing, friend. As the Alzette runs.',
    effects: [{ type: 'grant', item: 'rod', quantity: 1 }],
    options: [{ text: 'Walk away.', nextDialogId: 'start' }],
  },

  // --- The session: bite -> one choice -> a narrated haul ----------------------
  {
    id: 'session_bite',
    type: DialogType.description,
    // The handful is spent here, at the start: it is the bait for the whole session.
    text: 'You bait the hook and throw the line into the water. For a long while it lies still. Then it pulls tight, and something strong fights on the other end.',
    effects: [{ type: 'consume', item: 'worm', quantity: 1 }],
    options: [
      { text: 'Pull it in now.', nextDialogId: resolvePullNow },
      { text: 'Let it run a little.', nextDialogId: resolveLetRun },
    ],
  },

  // Safe play: a modest, sure haul. (Best fish granted first, so a full pack keeps the best.)
  {
    id: 'haul_safe_a',
    type: DialogType.description,
    text: 'You pull it in steady and lift out a fat **chub**. You stay by the water into the afternoon and take a **trout** more before the light goes. A fair day.',
    effects: [
      { type: 'grant', item: 'trout', quantity: 1 },
      { type: 'grant', item: 'chub', quantity: 1 },
    ],
    options: [AWAY],
  },
  {
    id: 'haul_safe_b',
    type: DialogType.description,
    text: 'You bring it up clean, a fat **chub**. The slow water gives one more **chub** before you leave. Plain fish, but they fill a pot.',
    effects: [
      { type: 'grant', item: 'chub', quantity: 1 },
      { type: 'grant', item: 'chub', quantity: 1 },
    ],
    options: [AWAY],
  },

  // The gamble pays off.
  {
    id: 'haul_big_pike',
    type: DialogType.description,
    text: 'You give it line, and it fights hard across the water. When it tires, you drag up a long **pike**. After that the fish come easy, and a **trout** and a **chub** join it in your bag.',
    effects: [
      { type: 'grant', item: 'pike', quantity: 1 },
      { type: 'grant', item: 'trout', quantity: 1 },
      { type: 'grant', item: 'chub', quantity: 1 },
    ],
    options: [AWAY],
  },
  {
    id: 'haul_big_eel',
    type: DialogType.description,
    text: 'You let it wind deep, then bring it up slow: a dark **eel**, thick as your wrist. The evening turns kind, and two **trout** follow it in before the light fails.',
    effects: [
      { type: 'grant', item: 'eel', quantity: 1 },
      { type: 'grant', item: 'trout', quantity: 1 },
      { type: 'grant', item: 'trout', quantity: 1 },
    ],
    options: [AWAY],
  },

  // The gamble fails: the one that got away.
  {
    id: 'got_away',
    type: DialogType.description,
    text: 'You let it run too long. One hard pull and the line goes slack, worm and all. Whatever it was, it beat you, and the fish keep their distance after that. You take home one small **chub** and little else.',
    effects: [{ type: 'grant', item: 'chub', quantity: 1 }],
    options: [AWAY],
  },

  // Busts: funny, world-building, no fish. The bait is already spent.
  {
    id: 'bust_boot',
    type: DialogType.description,
    text: 'You pull, sure of a fish. It is an old **boot**, heavy with river water. Nothing better comes all day. The river keeps its own jokes.',
    options: [AWAY],
  },
  {
    id: 'bust_eeltrap',
    type: DialogType.description,
    text: 'You heave up not a fish but a rotted **eel-trap**, half its sticks gone. Someone down in **Dommeldange** is a trap short, and none too pleased. You catch nothing after.',
    options: [AWAY],
  },
];
